import { SelectionChip } from '../../../components/ui/SelectionChip';
import { SelectionField } from '../../../components/ui/SelectionField';
import { LocationIcon } from '../../../components/ui/icons';
import { recordKey } from '../../collection/multiCollectionIdentity';
import { useEffect, useRef } from 'react';
import { Combobox } from '../../../components/ui/Combobox';
import { Location } from '../../../domain/location';
import { locationLabel, locationOptions } from './locationOptions';

export function LocationFilter({
  value,
  locations,
  onChange,
  label,
}: {
  value: string[];
  locations: Location[];
  onChange: (value: string[]) => void;
  label: string;
}) {
  const grouped = locationOptions(locations);
  const byKey = new Map(
    locations.map((location) => [recordKey(location), location]),
  );
  const labels = useRef(new Map<string, string>());
  useEffect(() => {
    locations.forEach((location) =>
      labels.current.set(recordKey(location), locationLabel(location)),
    );
  }, [locations]);
  const getLabel = (key: string) => {
    const location = byKey.get(key);
    return location
      ? locationLabel(location)
      : labels.current.get(key) || 'Selected location';
  };
  return (
    <SelectionField label={label}>
      <Combobox<string>
        multiple
        label={label}
        placeholder="Type to search locations…"
        sx={{ maxWidth: '100%' }}
        getOptionKey={(key) => key}
        getOptionText={(key) => byKey.get(key)?.name || getLabel(key)}
        addLabel="Add location"
        inputAriaLabel="Filter locations"
        options={grouped.options.map(recordKey)}
        groupBy={
          grouped.groupBy
            ? (key) => grouped.groupBy!(byKey.get(key)!)
            : undefined
        }
        getGroupLabel={grouped.getGroupLabel}
        getOptionLabel={getLabel}
        value={value}
        onChange={onChange}
      />
      {value.map((key) => (
        <SelectionChip
          key={key}
          label={getLabel(key)}
          icon={<LocationIcon />}
          onRemove={() =>
            onChange(value.filter((candidate) => candidate !== key))
          }
        />
      ))}
    </SelectionField>
  );
}
